/**
 * Pure helpers for reading the section-first auth-letter payer config doc.
 *
 * The stored doc shape is
 * `{ j_code_info: { <payer>: {...} }, prompt_provider_info: {...}, updated_at }`.
 * A payer is "disabled" when its entry lives under a section's
 * {@link DISABLED_KEY} sub-map instead of at the section's top level.
 * @module authLetterConfigUtils
 */
import { deleteField } from "firebase/firestore";

export type Dict = Record<string, unknown>;

/** Prompt sections held in the config doc, keyed by payer. */
export const SECTIONS = ["j_code_info", "prompt_provider_info"] as const;

/**
 * Reserved sub-map key holding disabled payers inside each section.
 *
 * Must not start and end with `__` — Firestore rejects those field names on
 * client writes. The agent's payer matcher looks up top-level payer keys only,
 * so entries nested here fall back to the `default` prompt.
 */
export const DISABLED_KEY = "disabled";

/**
 * Deep-clones a JSON-serialisable value.
 *
 * @param v - Value to clone
 * @return A structurally independent copy
 */
export const deepClone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/**
 * Type guard for a non-null, non-array plain object.
 *
 * @param v - Value to test
 * @return `true` when `v` is a plain object
 */
export const isPlainObject = (v: unknown): v is Dict =>
  Boolean(v) && typeof v === "object" && !Array.isArray(v);

/**
 * Sorts payers alphabetically, always keeping `default` first.
 *
 * @param a - First payer name
 * @param b - Second payer name
 * @return Negative, zero, or positive per `Array.prototype.sort`
 */
export const comparePayers = (a: string, b: string): number => {
  if (a.trim().toLowerCase() === "default") return -1;
  if (b.trim().toLowerCase() === "default") return 1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

/**
 * Returns the disabled-payer sub-map nested under a section, if any.
 *
 * @param doc - The config document
 * @param section - Section name to inspect
 * @return The `disabled` sub-map, or `{}` when absent
 */
export const disabledMap = (doc: Dict, section: string): Dict => {
  const m = doc[section];
  const d = isPlainObject(m) ? m[DISABLED_KEY] : undefined;
  return isPlainObject(d) ? d : {};
};

/**
 * Lists every payer across sections — enabled (top level) and disabled (nested).
 *
 * @param doc - The config document
 * @return Sorted, de-duplicated payer names
 */
export const getPayers = (doc: Dict): string[] => {
  const names = new Set<string>();
  for (const sec of SECTIONS) {
    const m = doc[sec];
    if (!isPlainObject(m)) continue;
    for (const k of Object.keys(m)) {
      if (k !== DISABLED_KEY) names.add(k);
    }
    Object.keys(disabledMap(doc, sec)).forEach((p) => names.add(p));
  }
  return Array.from(names).sort(comparePayers);
};

/**
 * Returns a payer's entry for a section, whether enabled or disabled.
 *
 * @param doc - The config document
 * @param section - Section name to read
 * @param payer - Payer whose entry is returned
 * @return The payer's section entry, or `{}` when absent
 */
export const payerSection = (
  doc: Dict,
  section: string,
  payer: string,
): Dict => {
  const m = doc[section];
  if (isPlainObject(m) && isPlainObject(m[payer])) return m[payer] as Dict;
  const disabled = disabledMap(doc, section)[payer];
  return isPlainObject(disabled) ? disabled : {};
};

/**
 * Reports whether a payer is enabled.
 *
 * A payer is enabled when its entry sits at the top level of any section, and
 * disabled only while it lives under a `disabled` sub-map. Drafts and
 * unknown payers default to enabled.
 *
 * @param doc - The config document
 * @param payer - Payer to check
 * @return `true` when the payer is enabled
 */
export const isEnabled = (doc: Dict, payer: string): boolean => {
  for (const sec of SECTIONS) {
    const m = doc[sec];
    if (isPlainObject(m) && isPlainObject(m[payer])) return true;
  }
  for (const sec of SECTIONS) {
    if (isPlainObject(disabledMap(doc, sec)[payer])) return false;
  }
  return true;
};

/**
 * Builds a toggle field patch that relocates `payer` within `orgDoc` only.
 *
 * Moves the payer's existing entry between each section's top level and its
 * `disabled` sub-map. Does not inject content from another org — so multi-org
 * toggles preserve each facility's own prompt.
 *
 * @param orgDoc - That org's current payer_prompt doc
 * @param payer - Payer to toggle
 * @param enabled - `true` to move to top level, `false` to nest under `disabled`
 * @return Field patch + locally-merged next doc, or `null` when nothing to move
 */
export const buildToggleFields = (
  orgDoc: Dict,
  payer: string,
  enabled: boolean,
): { fields: Dict; nextDoc: Dict } | null => {
  const nextDoc = deepClone(orgDoc);
  const fields: Dict = {};
  for (const sec of SECTIONS) {
    const secMap = isPlainObject(nextDoc[sec])
      ? (nextDoc[sec] as Dict)
      : (nextDoc[sec] = {});
    const disabled = isPlainObject(secMap[DISABLED_KEY])
      ? (secMap[DISABLED_KEY] as Dict)
      : (secMap[DISABLED_KEY] = {} as Dict);

    const from = enabled ? (disabled as Dict) : secMap;
    const to = enabled ? secMap : (disabled as Dict);
    if (!isPlainObject(from[payer])) continue;

    to[payer] = from[payer];
    delete from[payer];
    const topPath = `${sec}.${payer}`;
    const disabledPath = `${sec}.${DISABLED_KEY}.${payer}`;
    fields[enabled ? topPath : disabledPath] = to[payer];
    fields[enabled ? disabledPath : topPath] = deleteField();
  }
  return Object.keys(fields).length === 0 ? null : { fields, nextDoc };
};

/**
 * Builds an upload field patch for one org, respecting that org's On/Off state.
 *
 * Writes the new prompt under the top level when enabled, or under `disabled`
 * when off, and always clears the opposite path so a payer never ends up in
 * both places (or gets silently re-enabled).
 *
 * @param orgDoc - That org's current payer_prompt doc
 * @param payer - Payer receiving the upload
 * @param sections - Uploaded section payloads keyed by section name
 * @return Field patch + locally-merged next doc, or `null` when nothing to write
 */
export const buildUploadFields = (
  orgDoc: Dict,
  payer: string,
  sections: Partial<Record<(typeof SECTIONS)[number], Dict>>,
): { fields: Dict; nextDoc: Dict } | null => {
  const nextDoc = deepClone(orgDoc);
  const enabled = isEnabled(orgDoc, payer);
  const fields: Dict = {};

  for (const sec of SECTIONS) {
    const uploaded = sections[sec];
    if (uploaded === undefined) continue;
    const entry: Dict = isPlainObject(uploaded) ? deepClone(uploaded) : {};
    const secMap = isPlainObject(nextDoc[sec])
      ? (nextDoc[sec] as Dict)
      : (nextDoc[sec] = {});
    const topPath = `${sec}.${payer}`;
    const disabledPath = `${sec}.${DISABLED_KEY}.${payer}`;

    if (enabled) {
      secMap[payer] = entry;
      const disabled = isPlainObject(secMap[DISABLED_KEY])
        ? (secMap[DISABLED_KEY] as Dict)
        : undefined;
      if (disabled) delete disabled[payer];
      fields[topPath] = entry;
      fields[disabledPath] = deleteField();
    } else {
      const disabled = isPlainObject(secMap[DISABLED_KEY])
        ? (secMap[DISABLED_KEY] as Dict)
        : (secMap[DISABLED_KEY] = {} as Dict);
      (disabled as Dict)[payer] = entry;
      delete secMap[payer];
      fields[disabledPath] = entry;
      fields[topPath] = deleteField();
    }
  }

  return Object.keys(fields).length === 0 ? null : { fields, nextDoc };
};
